import React, { useEffect } from 'react';

/**
 * Adaptador local con la firma de `PopupPetra` (@ole/react-library-agents).
 *
 * Hoy renderiza el markup Aura del prototipo: overlay `.ovl.on` + panel `.modal.on` + `.mclose`,
 * con Escape y click-afuera manejados acá (antes vivían en PortalModals y en cada modal).
 * Migración (OUW-72): reemplazar el `return` por `<PopupPetra title footer onClose width …>`.
 * PopupPetra renderiza inline (sin portal), así que los estilos scoped en `.cp-root` le aplican.
 *
 * Divergencias respecto a `PopupPetraProps` (docs/client-portal/DS-MIGRATION.md):
 * - `title` / `subtitle`: el mock usa `eyebrow + h2` dentro de `children` (no hay equivalente Petra
 *   para el eyebrow coloreado). Se aceptan por compatibilidad de firma; si se pasan, se renderizan
 *   como `<h2>` / `<p>` al inicio del cuerpo.
 * - `footer`: se renderiza dentro de `.mfoot` (Aura). En Petra va en su propio pie con divisor.
 * - `id`: id del panel (`m-upload`, …) para trazabilidad con el prototipo.
 * - `closeLabel`: aria-label del botón cerrar (copy local; Petra lo trae fijo).
 */
export type PortalModalProps = {
  onClose: () => void;
  children?: React.ReactNode;
  title?: string;
  subtitle?: string;
  footer?: React.ReactNode;
  footerDivider?: boolean;
  width?: number;
  closeOnOverlay?: boolean;
  showCloseButton?: boolean;
  /**
   * En false, Escape, la ✕ y el clic afuera no llaman a `onClose`.
   * Sirve mientras una acción ya empezó y cerrar dejaría el trámite a medias.
   */
  dismissable?: boolean;
  testId?: string;
  className?: string;
  // --- locales ---
  id?: string;
  closeLabel?: string;
};

export function PortalModal({
  onClose,
  children,
  title,
  subtitle,
  footer,
  footerDivider = true,
  width,
  closeOnOverlay = true,
  showCloseButton = true,
  dismissable = true,
  testId,
  className,
  id,
  closeLabel = 'Cerrar',
}: PortalModalProps) {
  useEffect(() => {
    if (!dismissable) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, dismissable]);

  return (
    <div
      className="ovl on"
      id="ovl"
      data-testid={testId}
      onClick={(e) => {
        if (dismissable && closeOnOverlay && e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={['modal on', className || ''].filter(Boolean).join(' ')}
        id={id}
        role="dialog"
        aria-modal="true"
        style={width ? { maxWidth: width } : undefined}
      >
        {showCloseButton && (
          <button
            className="mclose"
            type="button"
            onClick={() => {
              if (dismissable) onClose();
            }}
            disabled={!dismissable}
            aria-label={closeLabel}
          >
            ✕
          </button>
        )}
        {title && <h2>{title}</h2>}
        {subtitle && <p>{subtitle}</p>}
        {children}
        {footer && (
          <div className="mfoot" style={footerDivider ? undefined : { borderTop: 0, paddingTop: 0 }}>
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/** Empuja los botones del pie a la derecha (Aura `.mfoot .spacer`). Petra alinea a la derecha por defecto. */
export function PortalModalSpacer() {
  return <div className="spacer" />;
}
